/// <reference types="vite/types/importMeta.d.ts" />

import { useEffect, useState, useRef, useCallback } from 'react'
import { createConsumer, Subscription } from '@rails/actioncable'

export interface ZoneStatus {
  currentTrack?: any
  playing: boolean
  paused?: boolean
  currentPosition: number
  trackDuration?: number | null
  volume: number
  crossfade: number
  formattedPosition: string
  formattedDuration: string
}

export function useZoneStatus(zoneId: string) {
  const [status, setStatus] = useState<ZoneStatus | null>(null)
  const [isConnected, setIsConnected] = useState(false)
  const [lastUpdate, setLastUpdate] = useState<number>(0)
  const [error, setError] = useState<string | null>(null)
  const [isSeeking, setIsSeeking] = useState(false)
  const consumerRef = useRef<any>(null)
  const subscriptionRef = useRef<Subscription | null>(null)
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const lastValidStatusRef = useRef<ZoneStatus | null>(null)
  const seekTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Optimistic update function for immediate UI feedback
  const updateStatusOptimistically = useCallback((updates: Partial<ZoneStatus>) => {
    setStatus((current) => {
      if (!current) return null
      const updated = { ...current, ...updates }
      lastValidStatusRef.current = updated
      return updated
    })
  }, [])

  // Seek function with optimistic updates
  const seekToPosition = useCallback(
    async (position: number) => {
      if (!zoneId || isSeeking) return false

      setIsSeeking(true)
      setError(null)

      // Clear any existing seek timeout
      if (seekTimeoutRef.current) {
        clearTimeout(seekTimeoutRef.current)
      }

      try {
        // Optimistic update - immediately show new position
        updateStatusOptimistically({
          currentPosition: position,
          formattedPosition: formatTime(position),
        })

        // Send seek command to API
        const response = await fetch(`/api/zones/${zoneId}/seek`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ position }),
        })

        if (!response.ok) {
          throw new Error(`Seek failed: ${response.statusText}`)
        }

        // Set timeout to clear seeking state
        seekTimeoutRef.current = setTimeout(() => {
          setIsSeeking(false)
        }, 2000) // 2 seconds max for seek to complete

        return true
      } catch (error) {
        console.error('Seek error:', error)
        setError(error instanceof Error ? error.message : 'Seek failed')
        setIsSeeking(false)
        return false
      }
    },
    [zoneId, isSeeking, updateStatusOptimistically]
  )

  // Helper function to format time
  const formatTime = useCallback((seconds: number): string => {
    if (!seconds || seconds < 0) return '0:00'
    const minutes = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${minutes}:${secs.toString().padStart(2, '0')}`
  }, [])

  // Validate and merge incoming status to prevent invalid states
  const mergeStatus = useCallback(
    (newStatus: ZoneStatus): ZoneStatus => {
      const current = lastValidStatusRef.current

      if (!current) {
        return newStatus
      }

      const timeDiff = Date.now() - lastUpdate
      const positionDiff = newStatus.currentPosition - current.currentPosition
      const trackChanged = newStatus.currentTrack?.id !== current.currentTrack?.id

      // Detect if this might be a seek operation
      const isLikelySeek = Math.abs(positionDiff) > 5 // More than 5 second jump
      const isPlayingStateChanged = newStatus.playing !== current.playing

      // Allow position updates if:
      // 1. Track changed (new track)
      // 2. Likely a seek operation (large position jump)
      // 3. Playing state explicitly changed
      // 4. Position is moving forward naturally
      // 5. Player is stopped/paused (position can be anything)

      if (trackChanged || isLikelySeek || isPlayingStateChanged || !current.playing) {
        // Allow the update as-is
        return newStatus
      }

      // For normal progress updates, validate forward movement
      if (current.playing && newStatus.playing) {
        const expectedPosition = current.currentPosition + timeDiff / 1000
        const maxExpectedPosition = expectedPosition + 2 // Allow 2 second buffer

        // Prevent backwards movement during normal playback
        if (newStatus.currentPosition < current.currentPosition - 1) {
          console.warn('Preventing backwards position jump during playback')
          return { ...newStatus, currentPosition: current.currentPosition }
        }

        // Prevent unrealistic forward jumps during normal playback
        if (newStatus.currentPosition > maxExpectedPosition && timeDiff < 5000) {
          console.warn('Preventing unrealistic forward position jump')
          return { ...newStatus, currentPosition: expectedPosition }
        }
      }

      // Prevent position resets to 0 during playback unless explicitly seeking
      if (
        newStatus.currentPosition === 0 &&
        current.currentPosition > 0 &&
        newStatus.playing &&
        !trackChanged &&
        !isPlayingStateChanged
      ) {
        console.warn('Preventing unexpected position reset to 0')
        return { ...newStatus, currentPosition: current.currentPosition }
      }

      return newStatus
    },
    [lastUpdate]
  )

  useEffect(() => {
    if (!zoneId) {
      console.warn(`ActionCable: No zoneId provided`)
      setError('No zone ID provided')
      return
    }

    setError(null)

    // Clear any existing reconnection timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
    }

    const connectToZone = () => {
      console.log(`ActionCable: Connecting to zone ${zoneId}`)

      const cableUrl = import.meta.env.VITE_CABLE_URL as string | undefined

      // Reuse existing consumer if available and connected
      if (!consumerRef.current) {
        consumerRef.current = cableUrl ? createConsumer(cableUrl) : createConsumer()
      }

      const consumer = consumerRef.current

      try {
        subscriptionRef.current = consumer.subscriptions.create(
          { channel: 'ZoneStatusChannel', zone_id: zoneId },
          {
            connected() {
              console.log(`ActionCable: Connected to zone ${zoneId}`)
              setIsConnected(true)
              setError(null)
            },
            disconnected() {
              console.log(`ActionCable: Disconnected from zone ${zoneId}`)
              setIsConnected(false)
              setError('Disconnected from server')

              // Auto-reconnect after 1 second
              reconnectTimeoutRef.current = setTimeout(() => {
                console.log(`ActionCable: Attempting to reconnect to zone ${zoneId}`)
                connectToZone()
              }, 1000)
            },
            rejected() {
              console.error(`ActionCable: Subscription rejected for zone ${zoneId}`)
              setIsConnected(false)
              setError('Subscription rejected')
            },
            received: (data: ZoneStatus) => {
              try {
                // Only log in development to avoid production console spam
                if (import.meta.env.DEV && Math.random() < 0.1) {
                  // Log only 10% of messages
                  console.log(`ActionCable: Received update for zone ${zoneId}`, {
                    playing: data.playing,
                    position: data.currentPosition,
                    volume: data.volume,
                    track: data.currentTrack?.id,
                  })
                }

                // Validate and merge the status
                const mergedStatus = mergeStatus(data)
                setStatus(mergedStatus)
                setLastUpdate(Date.now())
                lastValidStatusRef.current = mergedStatus
                setError(null)

                // Clear seeking state if position update confirms seek
                if (isSeeking && seekTimeoutRef.current) {
                  clearTimeout(seekTimeoutRef.current)
                  setIsSeeking(false)
                }
              } catch (error) {
                console.error(`Error processing status update:`, error)
                setError('Error processing update')
              }
            },
          }
        )
      } catch (error) {
        console.error(`ActionCable: Failed to create subscription for zone ${zoneId}:`, error)
        setIsConnected(false)
        setError('Failed to create subscription')
      }
    }

    connectToZone()

    return () => {
      console.log(`ActionCable: Cleaning up subscription for zone ${zoneId}`)

      // Clear reconnection timeout
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
        reconnectTimeoutRef.current = null
      }

      // Clear seek timeout
      if (seekTimeoutRef.current) {
        clearTimeout(seekTimeoutRef.current)
        seekTimeoutRef.current = null
      }

      try {
        if (subscriptionRef.current && consumerRef.current) {
          consumerRef.current.subscriptions.remove(subscriptionRef.current)
          subscriptionRef.current = null
        }

        if (consumerRef.current) {
          consumerRef.current.disconnect()
          consumerRef.current = null
        }
      } catch (error) {
        console.error(`ActionCable: Error during cleanup for zone ${zoneId}:`, error)
      }

      setIsConnected(false)
      setError(null)
    }
  }, [zoneId, mergeStatus])

  return {
    status,
    isConnected,
    error,
    lastUpdate,
    isSeeking,
    updateStatusOptimistically,
    seekToPosition,
  }
}
